import { useEffect, useRef, useState } from 'react';
import { X, AlertCircle, Search, ChevronDown, Building2, Check, Users } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';

interface AmForMove {
  _id: string;
  email: string;
  profile?: { firstName?: string; lastName?: string };
  distributorData?: { companyName?: string; supervisorId?: string };
}

interface Supervisor {
  _id: string;
  email: string;
  profile?: { firstName?: string; lastName?: string };
  supervisorData?: { companyName?: string };
}

interface Props {
  am: AmForMove;
  onClose: () => void;
  onSuccess: () => void;
}

export function MoveAmSupervisorModal({ am, onClose, onSuccess }: Props) {
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  const [loadingSups, setLoadingSups] = useState(false);
  const [selectedSupId, setSelectedSupId] = useState<string>('');
  const [selectedSupCache, setSelectedSupCache] = useState<Supervisor | null>(null);
  const [supDropdownOpen, setSupDropdownOpen] = useState(false);
  const [supSearch, setSupSearch] = useState('');
  const supDropdownRef = useRef<HTMLDivElement>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Named for display; an id stands in only if the server could not resolve a name.
  const [missingProducts, setMissingProducts] = useState<Array<{ _id: string; name: string }>>([]);
  const [costFloorConflicts, setCostFloorConflicts] = useState<
    Array<{ _id: string; name: string; childPrice: number; parentCost: number }>
  >([]);
  const [preflightLoading, setPreflightLoading] = useState(false);

  /** No supervisor today — this move also converts the chain to 3-tier. */
  const isAdminDirect = !am.distributorData?.supervisorId;

  // Close dropdown on outside click.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (supDropdownRef.current && !supDropdownRef.current.contains(event.target as Node)) {
        setSupDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Only the latest search may fill the list; a late answer to an older one is dropped.
  const fetchSeq = useRef(0);
  const fetchSupervisors = async (search: string) => {
    const mine = ++fetchSeq.current;
    setLoadingSups(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (search.trim()) params.set('search', search.trim());
      const res = await apiClient.get(`/users/supervisors?${params.toString()}`);
      if (mine !== fetchSeq.current) return;
      const raw = (res as any)?.data;
      const list: Supervisor[] = Array.isArray(raw)
        ? raw
        : (raw?.data || raw?.users || []);
      setSupervisors(list.filter(s => s._id !== am.distributorData?.supervisorId));
    } catch {
      if (mine === fetchSeq.current) setError('Failed to load supervisors');
    } finally {
      if (mine === fetchSeq.current) setLoadingSups(false);
    }
  };

  // Initial load.
  useEffect(() => {
    fetchSupervisors('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [am._id]);

  // Debounced server-side search while the dropdown is open.
  useEffect(() => {
    if (!supDropdownOpen) return;
    const handle = setTimeout(() => {
      fetchSupervisors(supSearch);
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supSearch, supDropdownOpen]);

  useEffect(() => {
    if (!selectedSupId) {
      setMissingProducts([]);
      setCostFloorConflicts([]);
      return;
    }
    setPreflightLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await apiClient.adminMovePreflight(am._id, selectedSupId);
        setCostFloorConflicts(res.success && res.data ? res.data.costFloorConflicts || [] : []);
        if (res.success && res.data && !res.data.canMove) {
          const named = res.data.missingProducts
            ?? res.data.missingProductIds.map((id: string) => ({ _id: id, name: id }));
          setMissingProducts(named);
          setError(
            `New supervisor is missing ${named.length} product(s) the AM currently sells. Add them to the new supervisor's Bloom catalog first.`
          );
        } else {
          setMissingProducts([]);
        }
      } catch {
        // non-blocking
      } finally {
        setPreflightLoading(false);
      }
    })();
  }, [selectedSupId, am._id]);

  const dirty = selectedSupId.length > 0 && reason.trim().length >= 5 && missingProducts.length === 0;

  const handleSubmit = async () => {
    if (!dirty) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiClient.adminMoveAmSupervisor(am._id, selectedSupId, reason.trim());
      if (res.success) {
        toast.success('AM moved. Cost basis preserved under new supervisor.');
        onSuccess();
        onClose();
      } else if (res.code === 'products_unavailable_under_new_parent') {
        setMissingProducts(res.missingProducts ?? (res.missingProductIds || []).map(id => ({ _id: id, name: id })));
        setError(res.error || 'New supervisor lacks required products.');
      } else {
        setError(res.error || 'Failed to move AM');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to move AM');
    } finally {
      setSubmitting(false);
    }
  };

  const amName = [am.profile?.firstName, am.profile?.lastName].filter(Boolean).join(' ') || am.email;

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]"
      onClick={onClose}
    >
      <div
        className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {isAdminDirect
              ? `Assign ${amName} to a supervisor`
              : `Move ${amName} under a new supervisor`}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 flex gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              {error}
              {missingProducts.length > 0 && (
                <ul data-testid="missing-products" className="mt-2 list-disc pl-5 text-xs space-y-0.5">
                  {missingProducts.map(p => (
                    <li key={p._id}>{p.name}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {costFloorConflicts.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4 flex gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-medium">
                The new supervisor pays more than this AM does for{' '}
                {costFloorConflicts.length} product(s).
              </p>
              <p className="mt-1">
                The move still preserves the AM's cost basis, but the supervisor is left
                selling below their own cost on these, and orders will fail pricing
                validation until someone reprices. You can proceed and fix the pricing
                afterward.
              </p>
              <ul className="mt-2 space-y-0.5 text-xs">
                {costFloorConflicts.slice(0, 8).map(c => (
                  <li key={c._id}>
                    {c.name}: AM pays ${c.childPrice}, supervisor pays ${c.parentCost}
                  </li>
                ))}
                {costFloorConflicts.length > 8 && (
                  <li>…and {costFloorConflicts.length - 8} more</li>
                )}
              </ul>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New supervisor</label>
            <div ref={supDropdownRef} className="relative">
              <div
                onClick={() => { if (!submitting) setSupDropdownOpen(true); }}
                className={`w-full border rounded-md shadow-sm transition-all cursor-pointer ${
                  supDropdownOpen ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-300'
                } ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {supDropdownOpen ? (
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      autoFocus
                      value={supSearch}
                      onChange={e => setSupSearch(e.target.value)}
                      placeholder="Search by name, email, or company…"
                      className="w-full pl-9 pr-3 py-2 rounded-md focus:outline-none text-sm"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-between px-3 py-2">
                    {selectedSupCache && selectedSupCache._id === selectedSupId ? (
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <Building2 className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {[selectedSupCache.profile?.firstName, selectedSupCache.profile?.lastName].filter(Boolean).join(' ') || selectedSupCache.email}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {selectedSupCache.supervisorData?.companyName || selectedSupCache.email}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">— Select a supervisor —</span>
                    )}
                    <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  </div>
                )}
              </div>

              {supDropdownOpen && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
                  {loadingSups ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-500">Loading…</div>
                  ) : supervisors.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <Users className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">
                        {supSearch ? 'No supervisors match your search' : 'No supervisors available'}
                      </p>
                    </div>
                  ) : (
                    <ul className="py-1">
                      {supervisors.map(s => {
                        const isSelected = s._id === selectedSupId;
                        const name = [s.profile?.firstName, s.profile?.lastName].filter(Boolean).join(' ') || s.email;
                        const co = s.supervisorData?.companyName;
                        return (
                          <li
                            key={s._id}
                            onClick={() => {
                              setSelectedSupId(s._id);
                              setSelectedSupCache(s);
                              setSupDropdownOpen(false);
                              setSupSearch('');
                            }}
                            className={`px-3 py-2.5 cursor-pointer transition-colors ${
                              isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}`}>
                                  <Building2 className={`h-4 w-4 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                                </div>
                                <div className="min-w-0">
                                  <p className={`text-sm font-medium truncate ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                                    {name}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">
                                    {co ? `${co} • ${s.email}` : s.email}
                                  </p>
                                </div>
                              </div>
                              {isSelected && <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
            {preflightLoading && (
              <div className="text-xs text-gray-500 mt-1">Checking product availability…</div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-red-600">*</span>
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              minLength={5}
              maxLength={500}
              placeholder="e.g., reorg, supervisor departure, territory change"
              disabled={submitting}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="text-xs text-gray-500 mt-1">5–500 chars. Saved on the audit log.</div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-900">
            A new catalog will be auto-created under the new supervisor with the AM's current
            cost basis preserved. New supervisor can edit the prices afterward.
            {isAdminDirect && (
              <p className="mt-2">
                This AM buys direct from admin today. The move puts a supervisor between
                them, so the AM's admin catalogs are released and their cost comes from the
                new supervisor from now on.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!dirty || submitting || preflightLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Moving…' : 'Confirm move'}
          </button>
        </div>
      </div>
    </div>
  );
}
